import React, { useState } from "react";
import { FiEdit, FiSave, FiX } from "react-icons/fi";
import { useUpdateCompanyInfoMutation } from "store/company/companyApi";
import { useGetUserByIdQuery } from "store/user/userApi";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const CompanyDetails = ({ company, userEmail }) => {
  const authUser = useSelector((state) => state.auth.user);
  const { t } = useTranslation();

  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [editedCompany, setEditedCompany] = useState({
    name: company?.name || "",
    ownerName: company?.ownerName || "",
    phone: company?.phone || "",
    address: company?.address || "",
    note: company?.note || "",
    payment_method: company?.payment_method || "",
  });

  const [updateCompanyInfo, { isLoading: isCompanyLoading }] =
    useUpdateCompanyInfoMutation();
  const { refetch } = useGetUserByIdQuery(authUser._id, {
    skip: !authUser?._id,
  });

  const canEditCompany = userEmail === company?.email;

  const handleSaveCompany = async () => {
    try {
      await updateCompanyInfo({
        companyId: company._id,
        data: editedCompany,
      }).unwrap();
      setIsEditingCompany(false);
      await refetch();
    } catch (err) {
      console.error("Erreur lors de la mise à jour de l'entreprise :", err);
    }
  };

  const handleCancelCompany = () => {
    setEditedCompany({
      name: company?.name || "",
      ownerName: company?.ownerName || "",
      phone: company?.phone || "",
      address: company?.address || "",
      note: company?.note || "",
      payment_method: company?.payment_method || "",
    });
    setIsEditingCompany(false);
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
        <h5 style={{ color: "#0f84ff" }}>{t("profile.companyInfo")}</h5>
        {canEditCompany &&
          (!isEditingCompany ? (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditingCompany(true)}
              title={t("profile.editTitle")}>
              <FiEdit />
            </button>
          ) : (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={handleSaveCompany}
                disabled={isCompanyLoading}>
                <FiSave />
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={handleCancelCompany}
                disabled={isCompanyLoading}>
                <FiX />
              </button>
            </div>
          ))}
      </div>

      <div className="row">
        <div className="col-sm-6">
          <small className="text-muted">{t("profile.companyName")}</small>
          {isEditingCompany ? (
            <input
              className="form-control"
              value={editedCompany.name}
              onChange={(e) =>
                setEditedCompany({ ...editedCompany, name: e.target.value })
              }
            />
          ) : (
            <p className="text-dark">{company?.name}</p>
          )}
        </div>

        <div className="col-sm-6">
          <small className="text-muted">{t("profile.ownerName")}</small>
          <p className="text-dark">{company?.ownerName}</p>
        </div>

        <div className="col-sm-6">
          <small className="text-muted">{t("profile.companyEmail")}</small>
          <p className="text-dark">{company?.email}</p>
        </div>

        <div className="col-sm-6">
          <small className="text-muted">{t("profile.phone")}</small>
          {isEditingCompany ? (
            <input
              className="form-control"
              value={editedCompany.phone}
              onChange={(e) =>
                setEditedCompany({ ...editedCompany, phone: e.target.value })
              }
            />
          ) : (
            <p className="text-dark">{company?.phone}</p>
          )}
        </div>

        <div className="col-12">
          <small className="text-muted">{t("profile.address")}</small>
          {isEditingCompany ? (
            <input
              className="form-control"
              value={editedCompany.address}
              onChange={(e) =>
                setEditedCompany({
                  ...editedCompany,
                  address: e.target.value,
                })
              }
            />
          ) : (
            <p className="text-dark">{company?.address}</p>
          )}
        </div>

        <div className="col-sm-6 mt-3">
          <small className="text-muted">{t("profile.paymentMethod")}</small>
          {isEditingCompany ? (
            <input
              className="form-control"
              value={editedCompany.payment_method}
              onChange={(e) =>
                setEditedCompany({
                  ...editedCompany,
                  payment_method: e.target.value,
                })
              }
            />
          ) : (
            <p className="text-dark">
              {company?.payment_method
                ? company.payment_method
                : t("profile.noPaymentMethod")}
            </p>
          )}
        </div>

        <div className="col-sm-6 mt-3">
          <small className="text-muted">{t("profile.note")}</small>
          {isEditingCompany ? (
            <textarea
              className="form-control"
              rows="2"
              value={editedCompany.note}
              onChange={(e) =>
                setEditedCompany({ ...editedCompany, note: e.target.value })
              }
            />
          ) : (
            <p className="text-dark">
              {company?.note ? company.note : t("profile.noNote")}
            </p>
          )}
        </div>
      </div>

      <div className="col-12 mt-4">
        <h6 className="text-primary">{t("profile.companyDocuments")}</h6>
        <ul className="list-group">
          {company?.kbis && (
            <li className="list-group-item d-flex justify-content-between align-items-center">
              {t("profile.kbis")}
              <a
                href={`https://depense-sass.msit-demo.fr/uploads/${company.kbis}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary">
                {t("profile.viewDownload")}
              </a>
            </li>
          )}

          {company?.urssaf && (
            <li className="list-group-item d-flex justify-content-between align-items-center">
              {t("profile.urssaf")}
              <a
                href={`https://depense-sass.msit-demo.fr/uploads/${company.urssaf}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary">
                {t("profile.viewDownload")}
              </a>
            </li>
          )}

          {company?.fiscale && (
            <li className="list-group-item d-flex justify-content-between align-items-center">
              {t("profile.fiscale")}
              <a
                href={`https://depense-sass.msit-demo.fr/uploads/${company.fiscale}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary">
                {t("profile.viewDownload")}
              </a>
            </li>
          )}

          {company?.rcPro && (
            <li className="list-group-item d-flex justify-content-between align-items-center">
              {t("profile.rcPro")}
              <a
                href={`https://depense-sass.msit-demo.fr/uploads/${company.rcPro}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary">
                {t("profile.viewDownload")}
              </a>
            </li>
          )}

          {company?.extraFiles?.length > 0 &&
            company.extraFiles.map((extra, index) => (
              <li
                key={index}
                className="list-group-item d-flex justify-content-between align-items-center">
                {extra.title || `${t("profile.extraFile")} ${index + 1}`}
                <a
                  href={`https://depense-sass.msit-demo.fr/uploads/${extra.filePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary">
                  {t("profile.viewDownload")}
                </a>
              </li>
            ))}

          {!company?.kbis &&
            !company?.urssaf &&
            !company?.fiscale &&
            !company?.rcPro &&
            (!company?.extraFiles || company.extraFiles.length === 0) && (
              <li className="list-group-item text-muted">
                {t("profile.noDocuments")}
              </li>
            )}
        </ul>
      </div>
    </>
  );
};

export default CompanyDetails;
